import { FixtureSetting, Rack, RackItem, RackVariant, rackPieces, TrialCandidate } from './merchandisingTypes';

export function compatibleTrialRacks(candidate: TrialCandidate, racks: Rack[], fixtures: FixtureSetting[]): Rack[] {
  return racks.filter((rack) => fixtures.find((fixture) => fixture.id === rack.id)?.allowed_categories.includes(candidate.cat));
}

export function trialVariantLimit(variant: RackVariant, existingTarget = 0): number {
  const stock = Math.max(0, variant.on_hand) + Math.max(0, variant.office || 0) + Math.max(0, variant.transit || 0);
  return Math.max(existingTarget, Math.min(2, variant.can_order === true ? 2 : stock));
}

export function editableTrialCandidate(candidate: TrialCandidate, racks: Rack[]): TrialCandidate {
  const existing = racks.flatMap((rack) => rack.items).find((item) => item.sku === candidate.sku);
  const variants = new Map((existing?.variants || []).map((variant) => [variant.variant_id, variant]));
  candidate.variants.forEach((variant) => variants.set(variant.variant_id, { ...variants.get(variant.variant_id), ...variant }));
  return { ...candidate, variants: Array.from(variants.values()) };
}

export const renumberRackPositions = (racks: Rack[]): Rack[] => racks.map((rack) => ({ ...rack, items: rack.items.map((item, index) => ({ ...item, position: index + 1 })) }));

export function replacementEvidence(item: RackItem): string {
  const sales = item.history?.store_u30;
  const days = item.metrics?.display_days;
  return `門市近 30 天${sales == null ? '未取得' : `${sales} 件`} · 有效展示${days == null ? '未開始記錄' : `${days} 天`}`;
}

export function replacementOptions(rack: Rack, candidateSku: string): RackItem[] {
  const priority = (item: RackItem) => item.history?.store_u30 === 0 && (item.metrics?.display_days || 0) >= 14 ? 0 : 1;
  return rack.items.filter((item) => item.sku !== candidateSku).sort((a, b) => priority(a) - priority(b)
    || (a.history?.store_u30 ?? Number.MAX_SAFE_INTEGER) - (b.history?.store_u30 ?? Number.MAX_SAFE_INTEGER));
}

export function applyTrialSelection(racks: Rack[], candidate: TrialCandidate, fixtureId: string, targets: Record<string, number>, replacements: string[], fixtures: FixtureSetting[]): { racks?: Rack[]; error?: string } {
  candidate = editableTrialCandidate(candidate, racks);
  const existing = racks.flatMap((row) => row.items).find((item) => item.sku === candidate.sku);
  if (candidate.availability !== 'ready' && !existing) return { error: '這款目前無法加入，請先確認貨源。' };
  const rack = compatibleTrialRacks(candidate, racks, fixtures).find((row) => row.id === fixtureId);
  if (!rack) return { error: '請選擇可放這個品類的衣架。' };
  if (Object.keys(targets).some((id) => !candidate.variants.some((variant) => String(variant.variant_id) === id))) return { error: '只能選擇本次建議提供的色碼。' };
  const variants = candidate.variants.map((variant) => ({ ...variant, target: targets[String(variant.variant_id)] ?? existing?.variants.find((row) => row.variant_id === variant.variant_id)?.target ?? 0 }));
  if (variants.some((variant) => !Number.isSafeInteger(variant.target) || variant.target < 0 || variant.target > trialVariantLimit(variant, existing?.variants.find((row) => row.variant_id === variant.variant_id)?.target))) return { error: '請輸入允許範圍內的整數件數；無法訂貨的色碼不可超過現有與在途庫存。' };
  const total = variants.reduce((sum, variant) => sum + variant.target, 0);
  if (total < 1 || total > 20) return { error: '每款請選 1–20 件，先以少量試賣。' };
  if (replacements.some((sku) => !rack.items.some((item) => item.sku === sku && sku !== candidate.sku))) return { error: '只能替換這一架現有的款式。' };
  const moved = !rack.items.some((item) => item.sku === candidate.sku);
  const item: RackItem = { ...candidate, position: existing?.position || rack.items.length + 1, action: '人工試賣配置',
    selection_origin: 'staff_trial', selection_reason: '人工選擇少量試賣；原始銷售與展示依據保留。',
    variants: variants.filter((variant) => variant.target > 0).map((variant) => ({ ...variant, needed: Math.max(0, variant.target - variant.on_hand - (variant.transit || 0)), display_qty: moved ? 0 : variant.display_qty })),
  };
  const updated = racks.map((row) => ({ ...row, items: row.items.filter((current) => current.sku !== candidate.sku && !(row.id === fixtureId && replacements.includes(current.sku))) }));
  const target = updated.find((row) => row.id === fixtureId)!;
  target.items.push(item);
  if (rackPieces(target) > target.capacity) return { error: `這架仍超過容量 ${rackPieces(target) - target.capacity} 件，請明確選擇要替換的款式或減少試賣數量。` };
  return { racks: renumberRackPositions(updated) };
}

export function preparationEstimate(racks: Rack[]): { total: number; store: number; office: number; transit: number; purchase: number; unresolved: number } {
  const counts = { total: 0, store: 0, office: 0, transit: 0, purchase: 0, unresolved: 0 };
  racks.flatMap((rack) => rack.items).flatMap((item) => item.variants).forEach((variant) => {
    counts.total += variant.target;
    const store = Math.min(variant.target, Math.max(0, variant.on_hand)); counts.store += store;
    let gap = variant.target - store;
    const transit = Math.min(gap, Math.max(0, variant.transit || 0)); counts.transit += transit; gap -= transit;
    const office = Math.min(gap, Math.max(0, variant.office || 0)); counts.office += office; gap -= office;
    if (variant.office !== undefined && variant.transit !== undefined && variant.can_order === true) counts.purchase += gap;
    else counts.unresolved += gap;
  });
  return counts;
}
